/**
 * Allowlist of safe post-login redirect targets (prevents open-redirect). MUST cover every
 * real route under app/ — a missing entry silently falls back instead of erroring, which is
 * exactly what happened to /training-instance, /certificate and /sandbox-signup before this.
 * Paths here are basePath-stripped ('/courses/x'); '/academy' stays listed as the fallback
 * value itself. helpers/__tests__/safe-redirect.test.ts walks app/ to catch drift.
 * Exported for that test only — callers use getSafePath()/safeRedirect().
 */
export const ALLOWED_PATH_PATTERNS = [
  '/',
  '/academy',
  '/assessment',
  '/catalog',
  '/certificate',
  '/content',
  '/courses',
  '/explore',
  '/learning-paths',
  '/product',
  '/profile',
  '/reset-password',
  '/sandbox-signup',
  '/search',
  '/training-instance',
];

/** Rejects absolute/protocol-relative/javascript:/data: targets; otherwise requires an allowlist match. */
function isPathSafe(path: string): boolean {
  if (path.match(/^https?:\/\//i) || path.startsWith('//')) {
    return false;
  }

  if (path.match(/^(javascript|data|vbscript):/i)) {
    return false;
  }

  if (!path.startsWith('/')) {
    return false;
  }

  return ALLOWED_PATH_PATTERNS.some(pattern =>
    path === pattern || path.startsWith(`${pattern}/`) || path.startsWith(`${pattern}?`)
  );
}

/** Redirects to targetPath if allowlisted, else fallbackPath. */
export function safeRedirect(targetPath: string | undefined | null, fallbackPath: string = '/academy'): void {
  if (typeof window === 'undefined') return;

  const path = targetPath || fallbackPath;
  const safePath = isPathSafe(path) ? path : fallbackPath;
  
  window.location.href = safePath;
}

/** Same allowlist check as safeRedirect(), without navigating. */
export function getSafePath(targetPath: string | undefined | null, fallbackPath: string = '/academy'): string {
  const path = targetPath || fallbackPath;
  return isPathSafe(path) ? path : fallbackPath;
}
